<script setup>
import { ref, computed } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Avatar from 'next/avatar/Avatar.vue';
import ButtonNext from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import SidebarProfileMenuStatus from '../sidebar/SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import YearInReviewModal from 'dashboard/components-next/year-in-review/YearInReviewModal.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

const emit = defineEmits([
  'close',
  'openKeyShortcutModal',
  'showCreateAccountModal',
]);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const { uiSettings } = useUISettings();
const { accountId, currentAccount } = useAccount();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const globalConfig = useMapGetter('globalConfig/get');
const userAccounts = useMapGetter('getUserAccounts');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showYearInReviewModal = ref(false);

const bannerClosedKey = computed(() => `yir_closed_${accountId.value}_2025`);
const isBannerClosed = computed(
  () => uiSettings.value?.[bannerClosedKey.value] === true
);
const showYearInReviewMenuItem = computed(() => isBannerClosed.value);

const openYearInReviewModal = () => {
  showYearInReviewModal.value = true;
  emit('close');
};
const closeYearInReviewModal = () => {
  showYearInReviewModal.value = false;
};

const showChatSupport = computed(
  () =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
);

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value.name
);
const sortedCurrentUserAccounts = computed(() =>
  [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  )
);

const onChangeAccount = newId => {
  const accountUrl = `/app/accounts/${newId}/dashboard`;
  window.location.href = accountUrl;
};

const emitNewAccount = () => {
  emit('showCreateAccountModal');
};

const menuItems = computed(() => [
  {
    show: showYearInReviewMenuItem.value,
    showOnCustomBrandedInstance: false,
    label: t('SIDEBAR_ITEMS.YEAR_IN_REVIEW'),
    icon: 'i-lucide-gift',
    click: openYearInReviewModal,
  },
  {
    show: showChatSupport.value,
    showOnCustomBrandedInstance: false,
    label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
    icon: 'i-lucide-life-buoy',
    click: () => {
      window.$chatwoot.toggle();
    },
  },
  {
    show: true,
    showOnCustomBrandedInstance: true,
    label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
    icon: 'i-lucide-keyboard',
    click: () => {
      emit('openKeyShortcutModal');
    },
  },
  {
    show: true,
    showOnCustomBrandedInstance: true,
    label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
    icon: 'i-lucide-user-pen',
    link: { name: 'profile_settings_index' },
  },
  {
    show: true,
    showOnCustomBrandedInstance: true,
    label: t('SIDEBAR_ITEMS.APPEARANCE'),
    icon: 'i-lucide-palette',
    click: () => {
      const ninja = document.querySelector('ninja-keys');
      ninja.open({ parent: 'appearance_settings' });
    },
  },
  {
    show: true,
    showOnCustomBrandedInstance: false,
    label: t('SIDEBAR_ITEMS.DOCS'),
    icon: 'i-lucide-book',
    link: 'https://www.chatwoot.com/hc/user-guide/en',
    nativeLink: true,
    target: '_blank',
  },
  {
    show: true,
    showOnCustomBrandedInstance: false,
    label: t('SIDEBAR_ITEMS.CHANGELOG'),
    icon: 'i-lucide-scroll-text',
    link: 'https://www.chatwoot.com/changelog/',
    nativeLink: true,
    target: '_blank',
  },
  {
    show: currentUser.value.type === 'SuperAdmin',
    showOnCustomBrandedInstance: true,
    label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
    icon: 'i-lucide-castle',
    link: '/super_admin',
    nativeLink: true,
    target: '_blank',
  },
  {
    show: true,
    showOnCustomBrandedInstance: true,
    label: t('SIDEBAR_ITEMS.LOGOUT'),
    icon: 'i-lucide-power',
    click: Auth.logout,
  },
]);

const allowedMenuItems = computed(() =>
  menuItems.value.filter(item => item.show)
);
</script>

<template>
  <DropdownContainer align="right" class="relative" @close="emit('close')">
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center px-2 py-1 rounded-lg cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :class="{ 'bg-n-alpha-1': isOpen }"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          rounded-full
        />
        <div class="hidden md:block min-w-0 text-left">
          <div
            class="text-[13px] font-semibold leading-4 truncate text-n-slate-12 tracking-tight"
          >
            {{ currentUser.available_name }}
          </div>
          <div class="text-[11px] truncate text-n-slate-11 leading-4">
            {{ currentAccount.name }}
          </div>
        </div>
        <span
          class="i-lucide-chevron-down size-3.5 text-n-slate-10 flex-shrink-0 hidden md:inline-block"
        />
      </button>
    </template>
    <DropdownBody
      class="z-50 mt-1 w-80 max-h-[80vh] overflow-y-auto right-0 left-auto"
    >
      <SidebarProfileMenuStatus />
      <DropdownSeparator />

      <DropdownSection
        v-if="showAccountSwitcher"
        :title="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')"
      >
        <DropdownItem
          v-for="account in sortedCurrentUserAccounts"
          :id="`account-${account.id}`"
          :key="account.id"
          class="cursor-pointer"
          @click="onChangeAccount(account.id)"
        >
          <template #label>
            <div
              :for="account.name"
              class="text-left rtl:text-right flex gap-2 items-center"
            >
              <span
                class="text-n-slate-12 max-w-36 truncate min-w-0"
                :title="account.name"
              >
                {{ account.name }}
              </span>
              <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
              <span
                class="text-n-slate-11 max-w-24 truncate capitalize"
                :title="account.name"
              >
                {{
                  account.custom_role_id
                    ? account.custom_role.name
                    : account.role
                }}
              </span>
            </div>
            <Icon
              v-show="account.id === accountId"
              icon="i-lucide-check"
              class="text-n-teal-11 size-5"
            />
          </template>
        </DropdownItem>
        <DropdownItem v-if="globalConfig.createNewAccountFromDashboard">
          <ButtonNext
            color="slate"
            variant="faded"
            class="w-full"
            size="sm"
            @click="emitNewAccount"
          >
            {{ t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
          </ButtonNext>
        </DropdownItem>
      </DropdownSection>
      <DropdownSeparator v-if="showAccountSwitcher" />

      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>

  <YearInReviewModal
    :show="showYearInReviewModal"
    @close="closeYearInReviewModal"
  />
</template>
